// AGC: biometricPreferences.ts
// AGC: Local storage for biometric lock screen and prompt preferences.
// AGC: Mirrors Yitix plan - biometry acts as device lock, Firebase handles session.
// AGC: All preferences are device-local (not synced to Firebase).

const KEY_ENABLED = 'biometric-enabled'
const KEY_DISMISSED = 'biometric-prompt-dismissed'
const KEY_LAST_PROMPT = 'biometric-last-prompt'
const PROMPT_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000 // AGC: 7 days

/**
 * AGC: Check if biometric lock is enabled for this device
 */
export const isBiometricEnabled = (): boolean => {
  try {
    return localStorage.getItem(KEY_ENABLED) === 'true'
  } catch {
    return false
  }
}

/**
 * AGC: Enable or disable biometric lock
 */
export const setBiometricEnabled = (enabled: boolean): void => {
  try {
    localStorage.setItem(KEY_ENABLED, enabled ? 'true' : 'false')
  } catch (e) {
    console.warn('AGC: Could not save biometric preference:', e)
  }
}

/**
 * AGC: Check if user has permanently dismissed the "Enable Biometric?" prompt
 */
export const isBiometricPromptDismissed = (): boolean => {
  try {
    return localStorage.getItem(KEY_DISMISSED) === 'true'
  } catch {
    return false
  }
}

/**
 * AGC: Permanently dismiss the biometric prompt (Don't Ask Again)
 */
export const dismissBiometricPrompt = (): void => {
  try {
    localStorage.setItem(KEY_DISMISSED, 'true')
  } catch (e) {
    console.warn('AGC: Could not save dismissed preference:', e)
  }
}

/**
 * AGC: Should we show the "Enable Biometric?" prompt?
 * AGC: Yes only if: not enabled, not dismissed, and last prompt was >7 days ago (or never)
 */
export const shouldShowBiometricPrompt = (): boolean => {
  if (isBiometricEnabled()) return false
  if (isBiometricPromptDismissed()) return false
  try {
    const last = localStorage.getItem(KEY_LAST_PROMPT)
    if (!last) return true
    const ts = parseInt(last, 10)
    if (isNaN(ts)) return true
    return Date.now() - ts > PROMPT_INTERVAL_MS
  } catch {
    return true
  }
}

/**
 * AGC: Record that we showed the prompt (for "Not Now" - re-prompt in 7 days)
 */
export const recordBiometricPromptShown = (): void => {
  try {
    localStorage.setItem(KEY_LAST_PROMPT, String(Date.now()))
  } catch (e) {
    console.warn('AGC: Could not save prompt timestamp:', e)
  }
}

/**
 * AGC: Clear biometric preferences on logout (so different user starts fresh)
 * AGC: Keeps "dismissed" since it is device-level preference
 */
export const clearBiometricPreferences = (): void => {
  try {
    localStorage.removeItem(KEY_ENABLED)
    localStorage.removeItem(KEY_LAST_PROMPT)
    // AGC: Do NOT remove KEY_DISMISSED - user choice persists across accounts
  } catch (e) {
    console.warn('AGC: Could not clear biometric preferences:', e)
  }
}
